/**
 * Settings → Email and texts: how this business sends (D91).
 *
 * Each business sends quotes and invoices from its own mailbox. The password
 * and the texts token are write-only: the server never returns them, so the
 * boxes start empty and are sent only when somebody types in them.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, api } from "../lib/api";
import { type ChannelForm, channelForm, channelPatch, portValid } from "../lib/channels";
import type { ChannelSource, ChannelsPatch } from "../lib/types";
import { Card } from "./ui";

const MAIL_LINE: Record<ChannelSource, string> = {
  own: "Quotes and invoices go from your own mailbox.",
  server: "Using this server's mail settings. Set your own to send from your business's address.",
  none: "Not set up — Keystone gives you a link to send yourself.",
};

type Said = { tone: "info" | "crit"; text: string };

export function ChannelSettings() {
  const queryClient = useQueryClient();
  const channels = useQuery({ queryKey: ["org", "channels"], queryFn: () => api.org.channels() });
  const [draft, setDraft] = useState<Partial<ChannelForm>>({});
  const [said, setSaid] = useState<Said | null>(null);

  const save = useMutation({
    mutationFn: (body: ChannelsPatch) => api.org.saveChannels(body),
    onSuccess: (next, body) => {
      queryClient.setQueryData(["org", "channels"], next);
      // An empty secret is only ever sent by Remove, which must not throw
      // away anything else typed in the meantime.
      const removing = body.mail_password === "" || body.sms_auth_token === "";
      if (!removing) setDraft({});
      setSaid({
        tone: "info",
        text:
          body.mail_password === "" ? "Password removed." : body.sms_auth_token === "" ? "Token removed." : "Saved.",
      });
    },
    onError: (err) => setSaid({ tone: "crit", text: err instanceof ApiError ? err.message : "Couldn't save." }),
  });
  const test = useMutation({
    mutationFn: () => api.org.testMail(),
    onSuccess: (r) =>
      setSaid(r.delivered ? { tone: "info", text: `Sent to ${r.to}.` } : { tone: "crit", text: r.detail }),
    onError: (err) =>
      setSaid({ tone: "crit", text: err instanceof ApiError ? err.message : "Couldn't send the test." }),
  });

  if (channels.error) {
    return (
      <Card title="Email and texts">
        <p className="tiny muted">These are for the owner and office.</p>
      </Card>
    );
  }
  if (!channels.data)
    return (
      <Card title="Email and texts">
        <p className="tiny muted">Loading…</p>
      </Card>
    );

  const data = channels.data;
  const form: ChannelForm = { ...channelForm(data), ...draft };
  const patch = channelPatch(data, form);
  const dirty = Object.keys(patch).length > 0 || !portValid(form.mail_port);
  const set = <K extends keyof ChannelForm>(key: K, value: ChannelForm[K]) => {
    setSaid(null);
    setDraft((d) => ({ ...d, [key]: value }));
  };
  const text = (
    key: Exclude<keyof ChannelForm, "mail_use_tls">,
    label: string,
    extra?: { hint?: string; type?: string; placeholder?: string },
  ) => (
    <label className="field">
      <span>{label}</span>
      <input
        type={extra?.type}
        value={form[key]}
        placeholder={extra?.placeholder}
        autoCapitalize="none"
        autoCorrect="off"
        autoComplete={extra?.type === "password" ? "new-password" : "off"}
        onChange={(e) => set(key, e.target.value)}
      />
      {extra?.hint && <small className="tiny muted">{extra.hint}</small>}
    </label>
  );
  const remove = (what: "password" | "token", body: ChannelsPatch) => (
    <button
      className="linkish tiny"
      type="button"
      style={{ minHeight: 44 }}
      disabled={save.isPending}
      onClick={() => {
        if (window.confirm(`Remove the saved ${what}?`)) save.mutate(body);
      }}
    >
      Remove {what}
    </button>
  );

  return (
    <Card title="Email and texts" subtitle="How quotes and invoices reach your customers">
      <p className="tiny" style={{ lineHeight: 1.6 }}>
        {MAIL_LINE[data.mail_source]}
      </p>
      {data.mail_source === "own" && data.mail_username && !data.mail_password_set && (
        <p className="notice notice-warn" role="status">
          A username is set but no password — most mail servers will refuse to send.
        </p>
      )}

      <h3 className="sub-head">Email</h3>
      <div className="form-grid">
        {text("mail_host", "Mail server")}
        <label className="field">
          <span>Port</span>
          <input
            inputMode="numeric"
            value={form.mail_port}
            aria-invalid={!portValid(form.mail_port)}
            onChange={(e) => set("mail_port", e.target.value)}
          />
        </label>
        {text("mail_username", "Username")}
        <div>
          {text("mail_password", "Password", {
            type: "password",
            placeholder: data.mail_password_set ? "Set — leave blank to keep" : "",
          })}
          {data.mail_password_set && remove("password", { mail_password: "" })}
        </div>
        {text("mail_sender", "From address")}
        {text("mail_sender_name", "From name")}
      </div>
      <label
        className="field"
        style={{ flexDirection: "row", alignItems: "center", gap: 8, minHeight: 44, marginTop: 8 }}
      >
        <input
          type="checkbox"
          style={{ width: 22, minHeight: 22 }}
          checked={form.mail_use_tls}
          onChange={(e) => set("mail_use_tls", e.target.checked)}
        />
        Use TLS
      </label>

      <h3 className="sub-head">Texts (Twilio)</h3>
      <div className="form-grid">
        {text("sms_account_sid", "Account SID")}
        <div>
          {text("sms_auth_token", "Auth token", {
            type: "password",
            placeholder: data.sms_auth_token_set ? "Set — leave blank to keep" : "",
          })}
          {data.sms_auth_token_set && remove("token", { sms_auth_token: "" })}
        </div>
        {text("sms_sender", "Sender", { hint: "A number or a name." })}
      </div>

      {said && (
        <p
          className={`notice notice-${said.tone}`}
          role={said.tone === "crit" ? "alert" : "status"}
          style={{ marginTop: 10 }}
        >
          {said.text}
        </p>
      )}
      <div className="btn-row sticky-actions" style={{ marginTop: 12 }}>
        <button
          className="btn btn-primary"
          type="button"
          style={{ minHeight: 44 }}
          disabled={!dirty || !portValid(form.mail_port) || save.isPending}
          onClick={() => save.mutate(patch)}
        >
          {save.isPending ? "Saving…" : "Save"}
        </button>
        <button
          className="btn"
          type="button"
          style={{ minHeight: 44 }}
          disabled={dirty || test.isPending}
          title={dirty ? "Save first — the test uses what's saved." : undefined}
          onClick={() => {
            setSaid(null);
            test.mutate();
          }}
        >
          {test.isPending ? "Sending…" : "Send me a test email"}
        </button>
      </div>
    </Card>
  );
}
